import { useState, useRef, useCallback, useEffect } from "react"

import { ChartScatterData } from "@/app/report/common/constructor"
import { ChartStyle } from "@/app/report/common/chart/scatter"
import { Chart } from "@/app/report/common/chart/scatter"
import { DRAWER_WIDTH, REPORT_PADDING, REPORT_SECTION_PADDING } from "@/app/report/common/constants"
import { createDrawerStore } from "@/stores/global"
import { useTranslateMetricKey } from "@/hooks/metric/useTranslateKeyHook"
// import { VALUE_POSITION_TYPE } from "@/app/report/common/constants" TODO unused 제거

const DEFAULT_CHART_WIDTH = 590
const DEFAULT_CHART_HEIGHT = 400
const SCATTER_CHART_RATIO = 0.8
const SCATTER_ROW_GAP = 28

export default function ScatterChart({
  loading,
  chartData,
}: {
  loading: boolean
  chartData?: ChartScatterData
}) {
  // 차트 관련
  const elementChartContainer = useRef<HTMLDivElement>(null)
  const chartElements = useRef<HTMLDivElement>(null)
  const [chartWidth, setChartWidth] = useState<number>(0)
  const [chartHeight, setChartHeight] = useState<number>(0)
  const scatter = useRef<Chart | null>(null)

  // 리사이징 관련
  const { isOpen: isDrawerOpen, isMobile } = createDrawerStore()
  // handleResize 함수가 컴포넌트 렌더링 전에 실행되는 것을 방지하기 위해 useRef 사용
  const isMounted = useRef(false)

  const translateKey = useTranslateMetricKey()

  useEffect(() => {
    isMounted.current = true

    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    window.addEventListener("resize", handleResize) // 리사이즈

    // 모바일에서는 drawer 상태 변경 시 resize 하지 않음 (overlay이므로)
    const isMobileScreen = window.innerWidth < 1200
    if (!isMobileScreen) {
      handleResize()
    }

    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [isDrawerOpen])

  useEffect(() => {
    if (!loading && chartData) {
      drawChart()
    }
  }, [chartData, chartWidth, chartHeight, loading])

  const handleResize = () => {
    // PDF preview container 기준으로 계산하기
    const pdfContainer = document.querySelector(".ExportPdfPreviewCont")
    const containerWidth =
      pdfContainer?.clientWidth || document.querySelector(".Container")?.clientWidth
    if (containerWidth) {
      // 모바일에서는 drawer가 overlay이므로 width 조정하지 않음
      // store 타이밍 이슈로 window.innerWidth로 직접 체크
      const isMobileScreen = window.innerWidth < 1200
      const drawerOffset = isDrawerOpen && !isMobileScreen ? DRAWER_WIDTH : 0
      const currentChartWidth = pdfContainer
        ? (containerWidth - 40 - SCATTER_ROW_GAP) / 2 // PDF preview: 2열 레이아웃
        : containerWidth -
          drawerOffset -
          REPORT_PADDING -
          REPORT_SECTION_PADDING * 2 -
          SCATTER_ROW_GAP -
          1

      if (isMounted.current) {
        setChartWidth(currentChartWidth)
        setChartHeight(currentChartWidth * SCATTER_CHART_RATIO)
      }
    }
  }

  // 초기 마운트 시 resize 실행
  useEffect(() => {
    handleResize()
  }, [])

  const drawChart = useCallback(async () => {
    if (!elementChartContainer.current || !chartElements.current || !chartData) {
      return
    }

    const chartStyle = new ChartStyle({
      width: chartWidth || DEFAULT_CHART_WIDTH,
      height: chartHeight || DEFAULT_CHART_HEIGHT,
      color: chartData.settings.color,
    })

    scatter.current = new Chart({
      axisX: chartData.axisX,
      axisY: chartData.axisY,
      rows: chartData.data,
      chartStyle,
      mouseoverAxisXAvg: () => {},
      mouseoverAxisYAvg: () => {},
      mouseoutAxisXYAvg: () => {},
      mouseover: () => {},
      mousemove: () => {},
      mouseout: () => {},
      translateKey,
    })

    if (scatter.current && scatter.current.svg) {
      const node = scatter.current.svg.node()
      if (node) {
        // chart = node
        chartElements.current.innerHTML = ""
        chartElements.current.appendChild(node)
      }
    }
  }, [chartData, chartWidth, chartHeight])

  return (
    <div className="chart">
      <div ref={elementChartContainer} className="element-chart-container">
        <div ref={chartElements}></div>
      </div>
    </div>
  )
}
